import { useState } from "react";
import { LuCopy, LuCheck, LuKey, LuTerminal, LuShieldCheck } from "react-icons/lu";

/**
 * In-app API documentation shown under Developer API → Documentation.
 * Mirrors the public docs (igleads-API-Documentation.md) so backend developers can
 * authenticate with an API key and drive scrape jobs straight from their own server.
 */

const BASE_URL = "https://api.igleads.co/api";

const CURL = `# 1. Start a scrape (returns a jobId immediately)
curl -X POST "${BASE_URL}/scrapes" \\
  -H "Authorization: Bearer igl_sk_live_your_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{"target":"natgeo","scrapeType":"followers","scrapeGoal":"emails","emailLimit":200}'

# 2. Poll the job until status = completed, then read resultsEmails
curl -X GET "${BASE_URL}/scrapes/JOB_ID_HERE" \\
  -H "Authorization: Bearer igl_sk_live_your_key_here"`;

const NODE = `const BASE = "${BASE_URL}";
const KEY  = process.env.IGLEADS_API_KEY;            // igl_sk_live_...
const headers = { "Authorization": \`Bearer \${KEY}\`, "Content-Type": "application/json" };

async function run() {
  // 1. start
  const start = await fetch(\`\${BASE}/scrapes\`, {
    method: "POST", headers,
    body: JSON.stringify({ target: "natgeo", scrapeType: "followers", scrapeGoal: "emails", emailLimit: 200 }),
  }).then(r => r.json());
  const jobId = start.data.jobId;

  // 2. poll
  while (true) {
    await new Promise(r => setTimeout(r, 5000));
    const job = await fetch(\`\${BASE}/scrapes/\${jobId}\`, { headers }).then(r => r.json());
    if (["completed", "canceled"].includes(job.data.status)) {
      console.log(job.data.resultsEmails);          // 3. collect
      break;
    }
  }
}
run();`;

const PYTHON = `import os, time, requests

BASE = "${BASE_URL}"
KEY  = os.environ["IGLEADS_API_KEY"]                 # igl_sk_live_...
headers = {"Authorization": f"Bearer {KEY}", "Content-Type": "application/json"}

# 1. start
start = requests.post(f"{BASE}/scrapes", headers=headers, json={
    "target": "natgeo", "scrapeType": "followers", "scrapeGoal": "emails", "emailLimit": 200,
}).json()
job_id = start["data"]["jobId"]

# 2. poll
while True:
    time.sleep(5)
    job = requests.get(f"{BASE}/scrapes/{job_id}", headers=headers).json()
    if job["data"]["status"] in ("completed", "canceled"):
        print(job["data"]["resultsEmails"])          # 3. collect
        break`;

const SNIPPETS: Record<string, string> = { CURL, NODE, PYTHON };

interface EndpointRow { method: string; path: string; desc: string; }
const ENDPOINTS: EndpointRow[] = [
    { method: "POST", path: "/scrapes", desc: "Start a scrape job. Returns a jobId (status queued/processing)." },
    { method: "GET", path: "/scrapes/:id", desc: "Get a job + its results. Poll until status is completed." },
    { method: "GET", path: "/scrapes", desc: "List your 50 most recent jobs (no result arrays)." },
    { method: "GET", path: "/scrapes/usage", desc: "Audit log of every job with credit-spend totals." },
    { method: "PATCH", path: "/scrapes/:id/cancel", desc: "Cancel an in-progress job and refund unused credits." },
    { method: "DELETE", path: "/scrapes/:id", desc: "Delete a finished job from your history." },
    { method: "GET", path: "/auth/me", desc: "Your profile: id, email, name, creditsRemaining." },
    { method: "GET", path: "/credits/balance", desc: "Current credit balance." },
];

const PARAMS = [
    { field: "target", req: "yes", desc: "Username to scrape, or a hashtag prefixed with # (e.g. natgeo or #travel)." },
    { field: "scrapeType", req: "username targets", desc: "followers, following, or hashtag. Default followers." },
    { field: "scrapeGoal", req: "recommended", desc: "emails, profiles, or list — what you want back. Default emails." },
    { field: "emailLimit", req: "one of these", desc: "How many results to collect — min 1, no upper cap (used for emails goal)." },
    { field: "resultLimit", req: "one of these", desc: "How many results to collect — min 1, no upper cap (used for profiles/list)." },
    { field: "location", req: "optional", desc: "2-letter proxy region (in, us, gb) to bias results geographically." },
];

function methodColor(m: string) {
    switch (m) {
        case "GET": return "bg-green-100 text-green-700";
        case "POST": return "bg-blue-100 text-blue-700";
        case "PATCH": return "bg-amber-100 text-amber-700";
        case "DELETE": return "bg-red-100 text-red-700";
        default: return "bg-gray-100 text-gray-700";
    }
}

function CodeBlock({ code }: { code: string }) {
    const [copied, setCopied] = useState(false);
    const copy = () => {
        navigator.clipboard.writeText(code);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };
    return (
        <div className="relative group">
            <button
                onClick={copy}
                className="absolute top-3 right-3 p-2 rounded-lg bg-white/10 hover:bg-white/20 text-gray-300 transition-colors"
                title="Copy"
            >
                {copied ? <LuCheck size={16} className="text-green-400" /> : <LuCopy size={16} />}
            </button>
            <pre className="bg-[#0d1117] text-gray-200 rounded-2xl p-5 overflow-x-auto text-[13px] leading-relaxed font-mono">
                <code>{code}</code>
            </pre>
        </div>
    );
}

export default function DeveloperApiDocs() {
    const [lang, setLang] = useState<"CURL" | "NODE" | "PYTHON">("CURL");

    return (
        <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
            {/* Getting started */}
            <section className="bg-white rounded-3xl border border-gray-100 shadow-xl shadow-purple-900/5 p-6 md:p-8">
                <h2 className="text-xl font-bold text-gray-900">Getting Started</h2>
                <p className="text-gray-500 text-sm mt-2 max-w-2xl">
                    The igleads.co REST API lets you trigger lead-extraction campaigns against public
                    Instagram profiles, follower chains, and hashtag feeds programmatically — then pull
                    the results back into your own backend.
                </p>

                <div className="mt-5 grid md:grid-cols-2 gap-4">
                    <div className="rounded-2xl border border-gray-200 p-4">
                        <div className="text-xs font-bold uppercase tracking-wider text-gray-400">Root API Endpoint</div>
                        <div className="mt-1 font-mono text-sm text-blue-600 break-all">{BASE_URL}</div>
                    </div>
                    <div className="rounded-2xl border border-gray-200 p-4">
                        <div className="text-xs font-bold uppercase tracking-wider text-gray-400">Response Envelope</div>
                        <div className="mt-1 font-mono text-sm text-gray-600">{`{ success, message, data }`}</div>
                    </div>
                </div>
            </section>

            {/* Authentication */}
            <section className="bg-white rounded-3xl border border-gray-100 shadow-xl shadow-purple-900/5 p-6 md:p-8">
                <div className="flex items-center gap-2">
                    <LuKey className="text-blue-500" size={20} />
                    <h2 className="text-xl font-bold text-gray-900">Authentication</h2>
                </div>
                <p className="text-gray-500 text-sm mt-2 max-w-2xl">
                    Authenticate every request with the <span className="font-semibold">same header</span>.
                    The value can be an <span className="font-semibold">API key</span> (recommended for
                    backends) or a logged-in JWT — the server detects which automatically.
                </p>

                <div className="mt-4">
                    <CodeBlock code={`Authorization: Bearer igl_sk_live_your_key_here`} />
                </div>

                <div className="mt-4 grid md:grid-cols-2 gap-4 text-sm">
                    <div className="rounded-2xl bg-green-50 border border-green-100 p-4">
                        <div className="font-bold text-green-800">API key — use this for your backend</div>
                        <p className="text-green-700/80 mt-1">
                            Looks like <code className="font-mono">igl_sk_live_…</code>. Long-lived until you
                            revoke it. Create one in the <span className="font-semibold">Your Keys</span> tab —
                            the full secret is shown only once.
                        </p>
                    </div>
                    <div className="rounded-2xl bg-gray-50 border border-gray-200 p-4">
                        <div className="font-bold text-gray-700">JWT — the web session token</div>
                        <p className="text-gray-500 mt-1">
                            Issued by <code className="font-mono">/auth/login</code>, expires in 7 days and is
                            killed on logout. Fine for the app itself; not ideal for automation.
                        </p>
                    </div>
                </div>
            </section>

            {/* Quickstart with language tabs */}
            <section className="bg-white rounded-3xl border border-gray-100 shadow-xl shadow-purple-900/5 p-6 md:p-8">
                <div className="flex items-center gap-2 mb-4">
                    <LuTerminal className="text-indigo-500" size={20} />
                    <h2 className="text-xl font-bold text-gray-900">Quickstart — start, poll, collect</h2>
                </div>
                <p className="text-gray-500 text-sm mb-4 max-w-2xl">
                    A scrape is asynchronous: start a job, poll it until <code className="font-mono">status</code>
                    {" "}is <code className="font-mono">completed</code>, then read the result array matching your
                    goal (<code className="font-mono">resultsEmails</code>, <code className="font-mono">resultsProfiles</code>,
                    or <code className="font-mono">resultsLists</code>). Plan for roughly ~1 profile/second.
                </p>

                <div className="inline-flex rounded-xl bg-gray-100 p-1 mb-4">
                    {(["CURL", "NODE", "PYTHON"] as const).map(l => (
                        <button
                            key={l}
                            onClick={() => setLang(l)}
                            className={`px-4 py-1.5 text-sm font-bold rounded-lg transition-all ${lang === l ? "bg-white text-blue-600 shadow-sm" : "text-gray-500 hover:text-gray-700"}`}
                        >
                            {l === "CURL" ? "cURL" : l === "NODE" ? "Node.js" : "Python"}
                        </button>
                    ))}
                </div>
                <CodeBlock code={SNIPPETS[lang]} />
            </section>

            {/* Endpoints */}
            <section className="bg-white rounded-3xl border border-gray-100 shadow-xl shadow-purple-900/5 p-6 md:p-8">
                <h2 className="text-xl font-bold text-gray-900 mb-1">Endpoints</h2>
                <p className="text-gray-500 text-sm mb-4">
                    Prepend <code className="font-mono">{BASE_URL}</code> to each path. All require the
                    Authorization header.
                </p>
                <div className="overflow-x-auto rounded-2xl border border-gray-100">
                    <table className="w-full text-left text-sm">
                        <tbody className="divide-y divide-gray-100">
                            {ENDPOINTS.map(e => (
                                <tr key={e.method + e.path} className="hover:bg-gray-50/50">
                                    <td className="p-3 w-20 align-top">
                                        <span className={`px-2 py-0.5 rounded-md text-xs font-bold ${methodColor(e.method)}`}>{e.method}</span>
                                    </td>
                                    <td className="p-3 font-mono text-gray-800 whitespace-nowrap align-top">{e.path}</td>
                                    <td className="p-3 text-gray-500 align-top">{e.desc}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </section>

            {/* POST /scrapes params */}
            <section className="bg-white rounded-3xl border border-gray-100 shadow-xl shadow-purple-900/5 p-6 md:p-8">
                <h2 className="text-xl font-bold text-gray-900 mb-1">
                    <span className="px-2 py-0.5 rounded-md text-xs font-bold bg-blue-100 text-blue-700 align-middle mr-2">POST</span>
                    <span className="font-mono text-base align-middle">/scrapes</span> body
                </h2>
                <div className="overflow-x-auto rounded-2xl border border-gray-100 mt-3">
                    <table className="w-full text-left text-sm">
                        <thead>
                            <tr className="bg-gray-50 text-xs uppercase tracking-wider text-gray-500">
                                <th className="p-3 font-bold">Field</th>
                                <th className="p-3 font-bold">Required</th>
                                <th className="p-3 font-bold">Description</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {PARAMS.map(p => (
                                <tr key={p.field} className="hover:bg-gray-50/50">
                                    <td className="p-3 font-mono text-gray-800 align-top">{p.field}</td>
                                    <td className="p-3 text-gray-500 align-top whitespace-nowrap">{p.req}</td>
                                    <td className="p-3 text-gray-500 align-top">{p.desc}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </section>

            {/* Security / legal */}
            <section className="rounded-3xl border border-blue-200/50 bg-blue-50/50 p-6 flex items-start gap-3">
                <LuShieldCheck className="shrink-0 mt-0.5 text-blue-500" size={20} />
                <div className="text-sm text-blue-900/80">
                    <span className="font-bold block mb-1">Security & Compliance</span>
                    Treat API keys like passwords — never commit them or expose them client-side. Rotate by
                    revoking and creating a new key (revoking refunds unused pool credits). The API targets
                    <span className="font-semibold"> publicly available</span> data only; you are responsible
                    for compliant use in your jurisdiction.
                </div>
            </section>
        </div>
    );
}
